import React from "react";

const MAX_ATTENTION_ITEMS = 6;
const MAX_OWNER_BUCKETS = 6;
const MAX_RECENT_CHANGES = 4;

const formatDate = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not set";
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatFilterPills = (filters, data) => {
  const pills = [];
  if (data?.as_of || filters?.asOf) pills.push(`As at: ${formatDate(data?.as_of || filters?.asOf)}`);
  if (data?.timezone) pills.push(`Timezone: ${data.timezone}`);
  if (filters?.search) pills.push(`Search: ${filters.search}`);
  if (filters?.status) pills.push(`Status: ${filters.status}`);
  if (filters?.priority) pills.push(`Priority: ${filters.priority}`);
  if (filters?.category) pills.push(`Category: ${filters.category}`);
  if (filters?.owner) pills.push(`Owner: ${filters.owner}`);
  if (filters?.assignee) pills.push(`Assignee: ${filters.assignee}`);
  return pills.length > 0 ? pills : ["Current actions scope"];
};

export default function ActionsHealthPrintPreview({
  data,
  filters,
  screen = false,
  root = false,
  testId = "actions-health-print-preview",
}) {
  const kpis = [
    { key: "total", label: "Total", value: data?.total_actions ?? 0 },
    { key: "attention", label: "Attention", value: data?.attention_actions ?? 0 },
    { key: "overdue", label: "Overdue", value: data?.overdue_actions ?? 0 },
    { key: "blocked", label: "Blocked", value: data?.blocked_actions ?? 0 },
    { key: "due_soon", label: "Due soon", value: data?.due_soon_actions ?? 0 },
    { key: "missing_owner", label: "Missing owner", value: data?.missing_owner ?? 0 },
    { key: "missing_assignee", label: "Missing assignee", value: data?.missing_assignee ?? 0 },
    { key: "high_priority_open", label: "High priority open", value: data?.high_priority_open ?? 0 },
  ];
  const attentionItems = Array.isArray(data?.attention_items) ? data.attention_items.slice(0, MAX_ATTENTION_ITEMS) : [];
  const hiddenAttentionCount = Math.max((data?.attention_items?.length || 0) - attentionItems.length, 0);
  const ownerBuckets = Array.isArray(data?.attention_by_owner) ? data.attention_by_owner.slice(0, MAX_OWNER_BUCKETS) : [];
  const hiddenOwnerCount = Math.max((data?.attention_by_owner?.length || 0) - ownerBuckets.length, 0);
  const recentChanges = Array.isArray(data?.recent_changes) ? data.recent_changes.slice(0, MAX_RECENT_CHANGES) : [];
  const hiddenRecentCount = Math.max((data?.recent_changes?.length || 0) - recentChanges.length, 0);
  const filterPills = formatFilterPills(filters, data);

  return (
    <div
      className={screen ? "actions-health-print-screen-preview" : undefined}
      data-actions-health-print-root={root ? "true" : undefined}
      data-testid={testId}
    >
      <article className="actions-health-print-sheet" data-print-layout="dedicated">
        <header className="actions-health-print-header">
          <div>
            <h1 className="actions-health-print-title">Actions health snapshot</h1>
            <div className="actions-health-print-subtitle">
              {data?.scope_note || "Showing action health for actions matching the current filters."}
            </div>
            {data?.generated_at && (
              <div className="actions-health-print-subtitle">Generated: {formatDate(data.generated_at)}</div>
            )}
          </div>
          <div className="actions-health-print-pill-row">
            {filterPills.map((pill) => (
              <span key={pill} className="actions-health-print-pill">
                {pill}
              </span>
            ))}
          </div>
        </header>

        <section className="actions-health-print-kpi-grid">
          {kpis.map((metric) => (
            <div key={metric.key} className="actions-health-print-card">
              <div className="actions-health-print-label">{metric.label}</div>
              <div className="actions-health-print-kpi-value">{metric.value}</div>
            </div>
          ))}
        </section>

        <section className="actions-health-print-card">
          <div className="actions-health-print-section-header">
            <div className="actions-health-print-section-title">Needs attention</div>
            <div className="actions-health-print-meta">
              {data?.attention_actions ?? 0} action{data?.attention_actions === 1 ? "" : "s"}
            </div>
          </div>
          {attentionItems.length > 0 ? (
            <>
              <table className="actions-health-print-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Owner / Assignee</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {attentionItems.map((item) => (
                    <tr key={item.action_id}>
                      <td>
                        <strong>{item.action_title}</strong>
                        <div>{item.category || "Uncategorized"}</div>
                      </td>
                      <td>
                        {item.owner || "Unassigned"}
                        <div>{item.assignee || "Unassigned"}</div>
                      </td>
                      <td>{Array.isArray(item.reasons) && item.reasons.length > 0 ? item.reasons.join(", ") : "-"}</td>
                      <td>
                        {item.status || "Unknown"}
                        <div>{item.priority || "Not set"}</div>
                      </td>
                      <td>{item.due_date ? formatDate(item.due_date) : "No due date"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hiddenAttentionCount > 0 && (
                <div className="actions-health-print-meta">+ {hiddenAttentionCount} more attention items not shown</div>
              )}
            </>
          ) : (
            <div className="actions-health-print-empty">No actions currently need attention.</div>
          )}
        </section>

        <section className="actions-health-print-detail-grid">
          <div className="actions-health-print-card">
            <div className="actions-health-print-section-header">
              <div className="actions-health-print-section-title">Attention by owner</div>
            </div>
            {ownerBuckets.length > 0 ? (
              <div className="actions-health-print-list">
                {ownerBuckets.map((bucket) => (
                  <div key={bucket.owner} className="actions-health-print-list-row">
                    <span>{bucket.owner}</span>
                    <strong>{bucket.count}</strong>
                  </div>
                ))}
                {hiddenOwnerCount > 0 && (
                  <div className="actions-health-print-meta">+ {hiddenOwnerCount} more owners not shown</div>
                )}
              </div>
            ) : (
              <div className="actions-health-print-empty">No owner breakdown available.</div>
            )}
          </div>

          <div className="actions-health-print-card">
            <div className="actions-health-print-section-header">
              <div className="actions-health-print-section-title">Recently changed</div>
            </div>
            {recentChanges.length > 0 ? (
              <div className="actions-health-print-list">
                {recentChanges.map((item) => (
                  <div key={item.action_id} className="actions-health-print-list-row actions-health-print-list-row--stacked">
                    <span>
                      <strong>{item.action_title}</strong>
                      <div>{item.owner || "Unassigned"}</div>
                    </span>
                    <span>{formatDate(item.updated_at)}</span>
                  </div>
                ))}
                {hiddenRecentCount > 0 && (
                  <div className="actions-health-print-meta">+ {hiddenRecentCount} more recent changes not shown</div>
                )}
              </div>
            ) : (
              <div className="actions-health-print-empty">No recent changes to show.</div>
            )}
          </div>
        </section>

        {data?.completeness_warning && (
          <footer className="actions-health-print-footer">{data.completeness_warning}</footer>
        )}
        {data?.limitation_note && <footer className="actions-health-print-footer">{data.limitation_note}</footer>}
      </article>
    </div>
  );
}
